import {
  getLessonTypeConfig,
  isReadableOnDemandLessonType,
  isRichMediaLessonType,
  isLiveLikeLessonType,
} from "@saas/components/src/utils/lesson-type";

/**
 * 按目录顺序把 unit_list 拍平成一维课时数组。
 */
export function flattenLessons(unitList) {
  if (!Array.isArray(unitList)) return [];
  const lessons = [];
  unitList.forEach((unit) => {
    if (Array.isArray(unit?.lesson_list)) {
      lessons.push(...unit.lesson_list);
    }
  });
  return lessons;
}

/**
 * 这节课"现在点开就能看"吗。
 * 判定口径与 LessonList.getLessonStatus + VideoCard.isShowDialog 保持一致：
 * - 点播/音频/直播回放算可播视频，图文课（lesson_type 6）按同一发布规则算"可读"（切过去进阅读器）；
 *   作业和未知类型都不是；
 * - 未报名且不可试听 = 锁住；
 * - 点播/音频要过了发布时间；
 * - 直播类要回放已生成（有 record_url）。
 */
export function isPlayableLesson(
  lesson,
  { isRegistered = false, now = Date.now(), excludeRichMedia = false } = {}
) {
  if (!lesson || !lesson.id) return false;

  const { lesson_type: lessonType } = lesson;
  const lessonTypeConfig = getLessonTypeConfig(lessonType);
  if (!lessonTypeConfig.isKnown) return false;
  // 只挂视频播放器的容器（桌面端独立回放窗）没有阅读器，图文课对它不是"下一节能看的"
  if (excludeRichMedia && isRichMediaLessonType(lessonType)) return false;

  // 未报名时只有试听课时可看。
  // canTrial 是 CourseCard 渲染目录时打上的标记，这里不依赖它一定存在，
  // 用与 CourseCard 相同的原始口径再判一次（类型支持试听 + 配了试听时长）。
  if (!isRegistered) {
    // trial_duration：>0 分钟制（点播/音频），-1 完整课时（直播/图文课），0/缺省 不可试听
    const trialDuration = Number(lesson.trial_duration) || 0;
    const canTrial =
      !!lesson.canTrial || (lessonTypeConfig.canTrial && trialDuration !== 0);
    if (!canTrial) return false;
  }

  if (isReadableOnDemandLessonType(lessonType)) {
    const publishTime = Number(lesson.publish_time) || 0;
    return publishTime * 1000 <= now;
  }

  if (isLiveLikeLessonType(lessonType)) {
    // 回放没生成之前，直播课时不是"下一个能看的视频"
    return !!lesson.record_url;
  }

  return false;
}

/**
 * 找当前课时之后第一节能看的课时，可跨单元，不可播的（锁住 / 未发布 / 回放未生成 /
 * 作业）都跳过。找不到返回 null——调用方据此显示"已是最后一节"。
 */
export function findNextPlayableLesson(unitList, currentLessonId, options = {}) {
  if (currentLessonId === null || currentLessonId === undefined || currentLessonId === "") {
    return null;
  }

  const lessons = flattenLessons(unitList);
  const currentIndex = lessons.findIndex(
    (lesson) => String(lesson?.id) === String(currentLessonId)
  );
  if (currentIndex === -1) return null;

  for (let i = currentIndex + 1; i < lessons.length; i += 1) {
    if (isPlayableLesson(lessons[i], options)) {
      return lessons[i];
    }
  }
  return null;
}
